import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  optimizationsSlice,
  type Optimization,
} from '@bbkAdminRedux/rtkq/optimizations.slice';

type OptimizationEditorContextType = {
  optimization: Partial<Optimization>;
  setOptimization: React.Dispatch<React.SetStateAction<Partial<Optimization>>>;
  saveOptimization: () => Promise<{ opt_id: string }>;
  isSaving: boolean;
};

const OptimizationEditorContext = createContext<
  OptimizationEditorContextType | undefined
>(undefined);

type OptimizationEditorProviderProps = {
  initOptimization: Partial<Optimization>;
};

export const OptimizationEditorProvider: React.FC<
  React.PropsWithChildren<OptimizationEditorProviderProps>
> = ({ children, initOptimization }) => {
  const appKey = useSelector(selectAppId);
  const [optimization, setOptimization] =
    useState<Partial<Optimization>>(initOptimization);

  const [updateOptimization$, { isLoading: isUpdating }] =
    optimizationsSlice.endpoints.updateOptimization.useMutation();
  const [createOptimization$, { isLoading: isCreating }] =
    optimizationsSlice.endpoints.createOptimization.useMutation();

  const saveOptimization = useCallback((): Promise<{
    opt_id: string;
  }> => {
    if (!optimization) {
      return Promise.reject(
        new Error('Nothing to save. Optimization is not defined')
      );
    }
    if (!optimization.opt_id) {
      return createOptimization$({
        appKey,
        display_name: optimization.display_name,
        actions: optimization.actions,
        audience: optimization.audience,
        configurations: optimization.configurations,
      }).unwrap();
    }
    return updateOptimization$({
      appKey,
      optId: optimization.opt_id,
      display_name: optimization.display_name,
      actions: optimization.actions,
      audience: optimization.audience,
      configurations: optimization.configurations,
    }).unwrap();
  }, [appKey, createOptimization$, optimization, updateOptimization$]);

  const isSaving = isCreating || isUpdating;

  const contextValue: OptimizationEditorContextType = useMemo(
    () => ({
      optimization,
      setOptimization,
      saveOptimization,
      isSaving,
    }),
    [optimization, saveOptimization, isSaving]
  );

  return (
    <OptimizationEditorContext.Provider value={contextValue}>
      {children}
    </OptimizationEditorContext.Provider>
  );
};

export const useOptimizationEditor = (): OptimizationEditorContextType => {
  const context = useContext(OptimizationEditorContext);
  if (context === undefined) {
    throw new Error(
      'useOptimizationEditor must be used within an OptimizationEditorProvider'
    );
  }
  return context;
};
